import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, Dropdown, Spin } from 'antd';
import { 
  BellOutlined, 
  WarningOutlined, 
  ClockCircleOutlined, 
  InfoCircleOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import '../i18n';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('urlToken');
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (user?.role === 'Supplier' && token) {
      fetchNotifications();
      
      // ✅ Her 5 dakikada bir otomatik güncelle
      const interval = setInterval(() => {
        fetchNotifications();
      }, 5 * 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [user?.role, token]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/SupplierNotifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setNotifications(response.data.notifications || []);
      setNotificationSummary({
        count: response.data.count,
        critical: response.data.critical,
        warning: response.data.warning,
        info: response.data.info,
      });
    } catch (error) {
      console.error('Notification fetch error:', error);
      setNotifications([]);
      setNotificationSummary({ count: 0, critical: 0, warning: 0, info: 0 });
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // ✅ Notification icon ve renk belirleme
  const getNotificationIcon = (type, category) => {
    const iconStyle = { fontSize: '16px', marginRight: '8px' };
    
    switch (type) {
      case 'error':
        if (category === 'expired_certificate' || category === 'missing_certificate') {
          return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
        }
        return <WarningOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
      case 'warning':
        return <ClockCircleOutlined style={{ ...iconStyle, color: '#faad14' }} />;
      case 'info':
        return <InfoCircleOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
      default:
        return <CheckCircleOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
    }
  };

  // ✅ Kategori ikonları
  const getCategoryIcon = (category) => {
    const iconStyle = { fontSize: '14px' };
    
    if (category?.includes('certificate')) {
      return <FileTextOutlined style={iconStyle} />;
    }
    if (category?.includes('audit')) {
      return <CalendarOutlined style={iconStyle} />;
    }
    return <InfoCircleOutlined style={iconStyle} />;
  };

  // ✅ Notification dropdown menu
  const notificationMenu = {
    items: notifications.length > 0 ? [
      {
        key: 'header',
        label: (
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px', 
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: '#fafafa',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('notifications')}</span>
              <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                ({notificationSummary?.count || 0})
              </span>
            </div>
            {notificationSummary && (notificationSummary.critical > 0 || notificationSummary.warning > 0) && (
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginTop: '8px',
                fontSize: '12px',
              }}>
                {notificationSummary.critical > 0 && (
                  <span style={{ color: '#ff4d4f' }}>
                    <ExclamationCircleOutlined /> {notificationSummary.critical} {t('critical')}
                  </span>
                )}
                {notificationSummary.warning > 0 && (
                  <span style={{ color: '#faad14' }}>
                    <WarningOutlined /> {notificationSummary.warning} {t('warning')}
                  </span>
                )}
              </div>
            )}
          </div>
        ),
        disabled: true,
      },
      ...notifications.slice(0, 6).map((notif, index) => {
        const bgColor = 
          notif.type === 'error' ? '#fff1f0' : 
          notif.type === 'warning' ? '#fffbe6' : 
          '#f0f5ff';
        
        const borderColor = 
          notif.type === 'error' ? '#ffccc7' : 
          notif.type === 'warning' ? '#ffe58f' : 
          '#adc6ff';

        return {
          key: index,
          label: (
            <div style={{ 
              padding: '12px',
              borderBottom: index < Math.min(notifications.length - 1, 5) ? '1px solid #f0f0f0' : 'none',
              backgroundColor: bgColor,
              borderLeft: `3px solid ${borderColor}`,
              margin: '4px 0',
              borderRadius: '4px',
            }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <div style={{ marginTop: '2px' }}>
                  {getNotificationIcon(notif.type, notif.category)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '13px', 
                    marginBottom: '4px',
                    color: '#262626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    {getCategoryIcon(notif.category)}
                    {notif.title}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#595959',
                    lineHeight: '1.5',
                  }}>
                    {notif.message}
                  </div>
                  
                  {/* ✅ Ek bilgiler (varsa) */}
                  {notif.daysRemaining !== undefined && (
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#8c8c8c',
                      marginTop: '4px',
                      fontStyle: 'italic',
                    }}>
                      {notif.daysRemaining} {t('days_remaining')}
                    </div>
                  )}
                  
                  {notif.daysExpired !== undefined && (
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#ff4d4f',
                      marginTop: '4px',
                      fontWeight: '500',
                    }}>
                      {notif.daysExpired} {t('days_overdue')}
                    </div>
                  )}
                  
                  {notif.daysOverdue !== undefined && (
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#ff4d4f',
                      marginTop: '4px',
                      fontWeight: '500',
                    }}>
                      {notif.daysOverdue} {t('days_overdue')}
                    </div>
                  )}

                  {/* ✅ Eksik sertifika listesi */}
                  {notif.details && Array.isArray(notif.details) && (
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#595959',
                      marginTop: '6px',
                      paddingLeft: '8px',
                      borderLeft: '2px solid #d9d9d9',
                    }}>
                      {notif.details.map((detail, i) => (
                        <div key={i}>• {detail}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ),
        };
      }),
      {
        key: 'view-all',
        label: (
          <Link to="/main/customer" style={{ 
            display: 'block', 
            textAlign: 'center',
            color: '#5B8DEF',
            fontWeight: '600',
            padding: '12px 0',
            borderTop: '1px solid #f0f0f0',
            backgroundColor: '#fafafa',
          }}>
            {t('view_all_notifications')} →
          </Link>
        ),
      },
    ] : [
      {
        key: 'empty',
        label: (
          <div style={{ 
            padding: '40px 20px', 
            textAlign: 'center', 
            color: '#8c8c8c',
          }}>
            <CheckCircleOutlined style={{ 
              fontSize: '32px', 
              marginBottom: '12px',
              color: '#52c41a',
            }} />
            <div style={{ fontSize: '14px', fontWeight: '500' }}>
              {t('no_notifications')}
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {t('all_up_to_date')}
            </div>
          </div>
        ),
        disabled: true,
      },
    ],
  };

  const totalCount = (notificationSummary?.critical || 0) + 
                    (notificationSummary?.warning || 0) + 
                    (notificationSummary?.info || 0);

  return (
    <header className='header'>
      <a href='/' className='logo'>
        <img 
          className="logo-img site-logo-img" 
          src="https://daiichi.com/new_site/wp-content/uploads/2019/11/daiichi-logo-w.svg" 
          width="200" 
          alt="DAIICHI Elektronik" 
          title="Infotainment Systems" 
        />
      </a>

      <nav className='navbar'>
        <a href='/'>{t('home')}</a>
        <Link to="/info">{t('contact')}</Link>

        {/* ✅ NOTIFICATION BELL (Sadece Supplier için) */}
        {user?.role === 'Supplier' && (
          <Dropdown 
            menu={notificationMenu}
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{ 
              width: '420px',
              maxHeight: '600px',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: '8px',
            }}
            onOpenChange={(open) => {
              if (open) {
                fetchNotifications(); // Açıldığında güncelle
              }
            }}
          >
            <a style={{ 
              cursor: 'pointer', 
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 12px',
              transition: 'all 0.3s ease',
            }}>
              <Spin spinning={loading} size="small">
                <Badge 
                  count={totalCount} 
                  overflowCount={99}
                  style={{ 
                    backgroundColor: notificationSummary?.critical > 0 ? '#ff4d4f' : '#faad14',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                  offset={[-2, 2]}
                >
                  <BellOutlined style={{ 
                    fontSize: '22px', 
                    color: 'white',
                    transition: 'all 0.3s ease',
                  }} />
                </Badge>
              </Spin>
            </a>
          </Dropdown>
        )}

        {/* Language Flags */}
        <a onClick={() => changeLanguage('tr')} style={{ cursor: 'pointer' }}>
          <img src='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAARVBMVEX+AAD3AADwAAD+fHz9cHH7ZGT9WVn6UFDpAAD9oKD5Q0P5OTn2MzP1Kir7ubr65ub1Gxv69PTzDw/kAAD319ffAAD4iooXHQ3FAAAAYklEQVR4AT3HhW0EQRQD0Oc/KG3/dQYEYTg2O+4IQbTHydWt0fw2Sfz8Fuw51+U3On7a6/pc/as1UZLDyuq13lWOwpdPn3+v7XJiDD3DR1N87Qr5WXX9zyQ9opEIOwkmDgr/ZXASmpFRqe0AAAAASUVORK5CYII=' alt="TR" width="20" />
        </a>
        <a onClick={() => changeLanguage('en')} style={{ cursor: 'pointer' }}>
          <img src='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAALCAMAAABBPP0LAAAAt1BMVEWSmb66z+18msdig8La3u+tYX9IaLc7W7BagbmcUW+kqMr/q6n+//+hsNv/lIr/jIGMnNLJyOP9/fyQttT/wb3/////aWn+YWF5kNT0oqz0i4ueqtIZNJjhvt/8gn//WVr/6+rN1+o9RKZwgcMPJpX/VFT9UEn+RUX8Ozv2Ly+FGzdYZrfU1e/8LS/lQkG/mbVUX60AE231hHtcdMb0mp3qYFTFwNu3w9prcqSURGNDaaIUMX5FNW5wYt7AAAAAjklEQVR4AR3HNUJEMQCGwf+L8RR36ajR+1+CEuvRdd8kK9MNAiRQNgJmVDAt1yM6kSzYVJUsPNssAk5N7ZFKjVNFAY4co6TAOI+kyQm+LFUEBEKKzuWUNB7rSH/rSnvOulOGk+QlXTBqMIrfYX4tSe2nP3iRa/KNK7uTmWJ5a9+erZ3d+18od4ytiZdvZyuKWy8o3UpTVAAAAABJRU5ErkJggg==' alt="EN" width="20" />
        </a>
        <a onClick={() => changeLanguage('zh')} style={{ cursor: 'pointer' }}>
          <img src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyIiBoZWlnaHQ9IjgiIGZpbGw9IiNERTI5MTAiLz48cGF0aCBkPSJNMiAxTDMgMyAzLjUgMUw0IDMgNC41IDFNNiAyTDUuNSAzIDYgNC41IDYuNSAzdDdMLjI1IDMgNiA0LjUgNS41IDNMMiAxWiIgZmlsbD0iI0ZGREUwMCIvPjwvc3ZnPg==' width="16" alt="Chinese Flag" />
        </a>
      </nav>
    </header>
  );
};

export default Navbar;
